import React from 'react';
import clsx from 'clsx';
import { Box, Typography } from '@mui/material';
import { Column } from '../types';
import { getStickySx } from '../libs';
import { TableCell } from './TableCell';
import { mergeCellStyles } from './mergeCellStyles';

interface Props<Data extends Record<string, any>, Context extends any = any> {
  context: Context;
  loading: boolean;
  tableData: Data[];
  columns: Column<Data, Context>[];
}

export const TableFooter = <Data extends Record<string, any>, Context extends any = any>({
  columns,
  context,
}: Props<Data, Context>) => {
  if (!columns.some(({ renderFooter }) => renderFooter)) return null;

  const skipIndices = new Set<number>();

  columns.forEach((column, index) => {
    if (column.footerColSpan && column.footerColSpan > 1) {
      for (let i = 1; i < column.footerColSpan; i++) {
        skipIndices.add(index + i);
      }
    }
  });

  return (
    <Box className={clsx('listing-section', 'listing-footer')}>
      {columns.map((column, columnIndex) => {
        if (skipIndices.has(columnIndex)) return null;

        const colSpan = column.footerColSpan ?? 1;

        return (
          <TableCell
            key={`${column.footer}_${columnIndex}`}
            column={column}
            context={context}
            sx={{
              ...(colSpan > 1 ? { gridColumn: `span ${colSpan}` } : {}),
              ...getStickySx(column, columns, columnIndex),
              ...mergeCellStyles(column?.cellStyles, column?.footerStyles),
            }}
          >
            {column.renderFooter ? (
              column.renderFooter({
                column,
                context,
                columns,
                columnIndex,
                existingColumnsTypes: columns.map(({ columnType }) => columnType).filter(Boolean) as string[],
              })
            ) : (
              <Typography variant="subtitle2" component="span">
                {column.footer}
              </Typography>
            )}
          </TableCell>
        );
      })}
    </Box>
  );
};
